import { useLayoutEffect, useState } from 'react'
import { useUiStore } from '../../state/ui-store.js'

const OVERSCAN = 2
type Listener = () => void

// One scroll listener and layout observer per board, rather than one per cell.
// Droppables keep their full geometry; only card previews enter/leave the DOM.
const boards = new WeakMap<HTMLElement, ReturnType<typeof observeBoard>>()

function observeBoard(board: HTMLElement) {
  const listeners = new Map<HTMLElement, Listener>()
  let frame = 0
  const schedule = () => {
    if (frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      listeners.forEach((measure) => measure())
    })
  }
  const observer = new ResizeObserver(schedule)
  observer.observe(board)
  if (board.firstElementChild) observer.observe(board.firstElementChild)
  board.addEventListener('scroll', schedule, { passive: true })
  return {
    subscribe(element: HTMLElement, measure: Listener) {
      listeners.set(element, measure)
      observer.observe(element)
      measure()
      return () => {
        listeners.delete(element)
        observer.unobserve(element)
        if (listeners.size) return
        cancelAnimationFrame(frame)
        observer.disconnect()
        board.removeEventListener('scroll', schedule)
        boards.delete(board)
      }
    },
  }
}

/** `slotHeight` is the zoomed card slot: rects here are layout pixels, so it must match. */
export function useBoardCardWindow(element: HTMLElement | null, count: number, slotHeight: number) {
  const [range, setRange] = useState({ start: 0, end: 0 })
  const exporting = useUiStore((state) => state.exporting)
  const measure = () => {
    const board = element?.closest<HTMLElement>('[data-testid="board-scroller"]')
    if (!element || !board) return
    // Exporting screenshots the DOM, so every card has to be in it — a windowed board
    // would export with most of its cards silently missing.
    if (useUiStore.getState().exporting) {
      setRange((previous) => (previous.start === 0 && previous.end === count ? previous : { start: 0, end: count }))
      return
    }
    const viewport = board.getBoundingClientRect()
    const bounds = element.getBoundingClientRect()
    // Include adjacent columns for keyboard dragging across the viewport edge.
    const horizontallyVisible = bounds.right >= viewport.left - bounds.width &&
      bounds.left <= viewport.right + bounds.width
    const start = horizontallyVisible
      ? Math.min(count, Math.max(0, Math.floor((viewport.top - bounds.top) / slotHeight) - OVERSCAN))
      : 0
    const end = horizontallyVisible
      ? Math.max(start, Math.min(count, Math.ceil((viewport.bottom - bounds.top) / slotHeight) + OVERSCAN))
      : 0
    setRange((previous) => previous.start === start && previous.end === end ? previous : { start, end })
  }

  // Also measure after React reorders rows without changing the board's total size.
  useLayoutEffect(measure)
  useLayoutEffect(() => {
    const board = element?.closest<HTMLElement>('[data-testid="board-scroller"]')
    if (!element || !board) return
    let observed = boards.get(board)
    if (!observed) {
      observed = observeBoard(board)
      boards.set(board, observed)
    }
    return observed.subscribe(element, measure)
  }, [element, count, slotHeight, exporting])

  return { start: Math.min(count, range.start), end: Math.min(count, range.end) }
}
